import React, { useRef } from 'react';

import { motion } from 'framer-motion';
import { useAuthStore } from 'src/entities/user/store/useAuthStore';
import { UserAvatar } from 'src/shared/components';
import { User } from 'src/shared/types/user';

import { addToast, Spinner } from 'src/shared/components/ui';
import { ButtonV2 } from 'src/shared/components/ui';

import { useUpdateUserAvatar } from '../hooks/useUpdateUserAvatar';

interface UpdateAccountAvatarProps {
  user: User | null;
}

const UpdateAccountAvatar: React.FC<UpdateAccountAvatarProps> = ({ user }) => {
  const setUser = useAuthStore((state) => state.setUser);
  const userAvatarMutation = useUpdateUserAvatar();

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const onChangeAvatar = (file: File) => {
    userAvatarMutation.mutate(
      { avatar: file, id: user?.id },
      {
        onError: (error) => {
          addToast({
            color: 'danger',
            title: error?.message || 'Failed to update profile avatar.'
          });
        },
        onSuccess: (data: User) => {
          setUser({
            ...(user as User),
            avatar: data.avatar
          });
          addToast({
            color: 'success',
            title: 'Profile avatar has been updated.'
          });
        }
      }
    );
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (!file) return;

    const isValidType = ['image/png', 'image/jpeg', 'image/jpg'].includes(
      file.type
    );

    if (!isValidType) {
      addToast({
        color: 'warning',
        title: 'Only PNG, JPEG, and JPG files are allowed.'
      });

      return;
    }

    onChangeAvatar(file);
  };

  return (
    <>
      <div className="flex max-w-fit relative">
        <UserAvatar
          size="xl"
          name={(user?.username || user?.firstName) as string}
          image={user?.avatar}
        />
        {userAvatarMutation.isPending ? (
          <div className="flex flex-col absolute justify-center items-center z-30 inset-0 bg-black/30 rounded-full">
            <Spinner color="white" />
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            whileHover={{ opacity: 1 }}
            className="flex flex-col absolute items-center justify-center inset-0 p-4 gap-2 z-30 bg-black/30 rounded-full"
          >
            <ButtonV2
              color="default"
              size="sm"
              onPress={() => fileInputRef.current?.click()}
            >
              Change
            </ButtonV2>
          </motion.div>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept=".png, .jpeg, .jpg"
        className="hidden"
        onChange={handleFileChange}
      />
    </>
  );
};

export default UpdateAccountAvatar;
